/**
 * Critical-path classification.
 *
 * Implements `04-` §17 D-13 (isCritical threshold), D-15 (cycle members
 * omitted), D-12 (completed tasks marked non-critical when use_progress on).
 */

import type { NormalizedTaskID } from "../types";
import type { AnalysisGraph, SlackEntry } from "../graph/types";

/**
 * Mark each non-omitted task as critical (slack ≤ 0) or not.
 *
 *   isCritical[node] = totalSlack[node] <= 0
 *
 * D-12 override: completed tasks (node.isCompleted) are never critical even
 * if their slack would otherwise be ≤ 0. The flag is set in
 * `buildAnalysisGraph` only when `useProgress: true` AND `task.progress === 1`.
 *
 * D-15: cycle members are not in the slack map (caller built the omit set);
 * they have no entry here. Adapter returns false for omitted from
 * `gantt.isCriticalTask`.
 *
 * v1 quirk: `isCriticalLink(link) = isCriticalTask(link.source)`. That
 * derivation is in the adapter — this module returns per-task data only.
 */
export function computeCriticalPath(
  slack: ReadonlyMap<NormalizedTaskID, SlackEntry>,
  graph: AnalysisGraph
): ReadonlyMap<NormalizedTaskID, boolean> {
  const result = new Map<NormalizedTaskID, boolean>();
  for (const [id, entry] of slack) {
    const node = graph.nodes.get(id);
    if (node?.isCompleted) {
      // D-12 explicit override.
      result.set(id, false);
      continue;
    }
    result.set(id, entry.totalSlack <= 0);
  }
  return result;
}
